import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@material-tailwind/react';
import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useApi } from '../../../providers/ApiProvider';
import { GoodApiResponse, ErrorApiResponse } from '../../../types/api';
import { ProfilEntreprise } from '../../../types/auth';
import { LoadingAbsolute } from '../../LoadingAbsolute';
import { Convocation } from '../../../types/AdminRessources';
import nl2br from 'react-nl2br';
import { handleError } from '../../../utils/ErrorHandler';
import { FormationSessionDisplay } from '../../FormationSessionDisplay';
import { UserDisplay } from '../../UserDisplay';
import { useNavigate } from 'react-router-dom';
import { DownloadLink } from '../../DownloadLink';

/**
 * Ce composant permet d'afficher une Modale pour lister les convocations du salarié.
 *
 * @param visible - Précise si la modale et ouverte ou non. Provient du parent.
 * @param setVisible - Accesseur qui modifie la visibilité de la modale. Provient du parent.
 * @param profil - Profil de l'entreprise. Provient du parent.
 */
export const ModalConvocationsSalariesAdmin: FunctionComponent<{
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  profil: ProfilEntreprise | null;
}> = ({ visible, setVisible, profil }) => {
  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * On stocke l'état des document liés à la convocation.
   */
  const [convocations, setConvocations] = useState<Convocation[] | null>(null);

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Permet au composant de changer de page.
   */
  const navigate = useNavigate();

  /**
   * On va chercher les informations du profil dès que celui-ci change. S'il est null on supprime les documents.
   */
  useEffect(() => {
    if (profil === null) {
      setConvocations(null);
    } else {
      if (visible) {
        handleListeConvocations();
      }
    }
  }, [profil, visible]);

  /**
   * Récupère les documents du salarié et les stocke dans un état local.
   */
  const handleListeConvocations = () => {
    setApiIsLoading(true);

    client
      .get<GoodApiResponse<Convocation[]>, { params: { profil_id: ProfilEntreprise['id'] } }>('/admin/convocations', {
        params: {
          profil_id: (profil as ProfilEntreprise).id,
        },
      })
      .then((response) => {
        setConvocations(response.data.data);
      })
      .catch((error: ErrorApiResponse) => {
        handleError(error);
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  return (
    <Dialog open={visible} size='lg' handler={setVisible}>
      <DialogHeader>Convocations du salarié</DialogHeader>
      <DialogBody divider>
        <div>
          {profil ? (
            <Fragment>
              <UserDisplay user={profil.user} />
              <div>
                <div className='bg-white w-full overflow-x-auto overflow-y-auto h-96'>
                  <div className='flex flex-row justify-end'></div>
                  <table className='table-fixed min-w-full bg-white rounded-b-lg'>
                    <thead className='text-sm text-gray-500 text-left border-b'>
                      <tr>
                        <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Formation et session</th>
                        <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Fichier</th>
                        <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Description</th>
                        <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Date de convocation</th>
                        <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {convocations &&
                        convocations.map((convocation) => (
                          <tr
                            key={convocation.id}
                            className='border-gray-100 border-b hover:bg-gray-100'
                            onDoubleClick={() => navigate(`/admin/convocations/${convocation.id}`)}>
                            <td className='px-4 py-3'>
                              <FormationSessionDisplay formation={convocation.formation} groupe={convocation.groupe} />
                            </td>
                            <td className='px-4 py-3 text-gray-800 text-sm'>
                              {convocation.fichier && <DownloadLink fichier={convocation.fichier} titre={convocation.titre} />}
                            </td>
                            <td className='px-4 py-3 text-gray-800 text-sm'>{convocation.description ? nl2br(convocation.description) : ''}</td>
                            <td className='px-4 py-3 text-gray-800 text-sm'>
                              {convocation.date_convocation
                                ? new Intl.DateTimeFormat('default', {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                  }).format(new Date(convocation.date_convocation as string))
                                : ''}
                            </td>
                            <td className='px-4 py-3 text-gray-800 text-sm'>
                              {convocation.created_at
                                ? new Intl.DateTimeFormat('default', {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                  }).format(new Date(convocation.created_at as string))
                                : ''}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Fragment>
          ) : (
            <p></p>
          )}
          {apiIsLoading ? <LoadingAbsolute /> : ''}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button
          variant='text'
          color='gray'
          onClick={() => {
            setVisible(false);
          }}
          className='mr-1'>
          Fermer
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
